import React, { useState } from "react";
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Switch,
  Tag,
  Popconfirm,
} from "antd";
import { Plus, Edit2, Trash2, DollarSign } from "lucide-react";
import {
  useGetServiceMetrics,
  useCreateServiceMetric,
  useUpdateServiceMetric,
  useDeleteServiceMetric,
  ServiceMetric,
  CreateServiceMetricDto,
} from "~/services/service-metrics";

const { Option } = Select;

interface ServiceMetricFormData {
  serviceType: ServiceMetric["serviceType"];
  name: string;
  baseRate: number;
  minimumCharge: number;
  minimumHours?: number;
  unitType?: string;
  isActive: boolean;
}

export const ServiceMetricsTab: React.FC = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingMetric, setEditingMetric] = useState<ServiceMetric | null>(
    null
  );
  const [form] = Form.useForm<ServiceMetricFormData>();

  const { data: serviceMetrics, isLoading } = useGetServiceMetrics();
  const createMutation = useCreateServiceMetric();
  const updateMutation = useUpdateServiceMetric();
  const deleteMutation = useDeleteServiceMetric();

  const handleCreate = () => {
    setEditingMetric(null);
    form.resetFields();
    form.setFieldsValue({
      isActive: true,
      baseRate: 0,
      minimumCharge: 0,
      minimumHours: 0,
    });
    setIsModalVisible(true);
  };

  const handleEdit = (record: ServiceMetric) => {
    setEditingMetric(record);
    form.setFieldsValue({
      serviceType: record.serviceType,
      name: record.name,
      baseRate: record.baseRate,
      minimumCharge: record.minimumCharge,
      minimumHours: record.minimumHours,
      unitType: record.unitType,
      isActive: record.isActive,
    });
    setIsModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    await deleteMutation.mutateAsync(id);
  };

  const handleSubmit = async (values: ServiceMetricFormData) => {
    try {
      const data: CreateServiceMetricDto = {
        serviceType: values.serviceType,
        name: values.name,
        baseRate: values.baseRate,
        minimumCharge: values.minimumCharge,
        minimumHours: values.minimumHours,
        unitType: values.unitType,
        isActive: values.isActive,
      };

      if (editingMetric) {
        await updateMutation.mutateAsync({ id: editingMetric.id, data });
      } else {
        await createMutation.mutateAsync(data);
      }

      setIsModalVisible(false);
      form.resetFields();
    } catch (error) {
      console.error("Failed to save service metric:", error);
    }
  };

  const columns = [
    {
      title: "Service Type",
      dataIndex: "serviceType",
      key: "serviceType",
      render: (type: string) => (
        <Tag color={getServiceTypeColor(type)}>
          {type.replace("_", " ").toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Base Rate",
      dataIndex: "baseRate",
      key: "baseRate",
      render: (rate: string) => (
        <span className="flex items-center">
          <DollarSign size={14} className="mr-1" />
          {Number(rate).toFixed(2)}
        </span>
      ),
    },
    {
      title: "Minimum Charge",
      dataIndex: "minimumCharge",
      key: "minimumCharge",
      render: (charge: string) => (
        <span className="flex items-center">
          <DollarSign size={14} className="mr-1" />
          {Number(charge).toFixed(2)}
        </span>
      ),
    },
    {
      title: "Minimum Hours",
      dataIndex: "minimumHours",
      key: "minimumHours",
      render: (hours?: number) => (hours ? `${hours} hrs` : "-"),
    },
    {
      title: "Unit Type",
      dataIndex: "unitType",
      key: "unitType",
      render: (unit?: string) => unit || "-",
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive: boolean) => (
        <Tag color={isActive ? "green" : "red"}>
          {isActive ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: ServiceMetric) => (
        <Space>
          <Button
            type="text"
            icon={<Edit2 size={16} />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Are you sure to delete this service metric?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" danger icon={<Trash2 size={16} />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  function getServiceTypeColor(type: string): string {
    const colors: Record<string, string> = {
      collection_delivery: "blue",
      site_service: "green",
      warehouse: "orange",
      fabrication: "purple",
      packing: "red",
    };
    return colors[type] || "default";
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium">Service Metrics</h3>
          <p className="text-gray-500">
            Manage service metrics for different services
          </p>
        </div>
        <Button type="primary" icon={<Plus size={16} />} onClick={handleCreate}>
          Add Service Metric
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={serviceMetrics || []}
        rowKey="id"
        loading={isLoading}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `Total ${total} items`,
        }}
      />

      <Modal
        title={editingMetric ? "Edit Service Metric" : "Create Service Metric"}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="serviceType"
            label="Service Type"
            rules={[{ required: true, message: "Please select service type" }]}
          >
            <Select placeholder="Select service type">
              <Option value="collection_delivery">Collection & Delivery</Option>
              <Option value="site_service">Site Service</Option>
              <Option value="warehouse">Warehouse</Option>
              <Option value="fabrication">Fabrication</Option>
              <Option value="packing">Packing</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: "Please enter metric name" }]}
          >
            <Input placeholder="Enter metric name" />
          </Form.Item>

          <Form.Item
            name="baseRate"
            label="Base Rate"
            rules={[{ required: true, message: "Please enter base rate" }]}
          >
            <InputNumber
              prefix="$"
              min={0}
              step={0.01}
              precision={2}
              className="w-full"
              placeholder="0.00"
            />
          </Form.Item>

          <Form.Item
            name="minimumCharge"
            label="Minimum Charge"
            rules={[{ required: true, message: "Please enter minimum charge" }]}
          >
            <InputNumber
              prefix="$"
              min={0}
              step={0.01}
              precision={2}
              className="w-full"
              placeholder="0.00"
            />
          </Form.Item>

          <Form.Item name="minimumHours" label="Minimum Hours (Optional)">
            <InputNumber
              min={0}
              step={0.5}
              precision={1}
              className="w-full"
              placeholder="0.0"
              addonAfter="hours"
            />
          </Form.Item>

          <Form.Item name="unitType" label="Unit Type (Optional)">
            <Select placeholder="Select unit type" allowClear>
              <Option value="hourly">Hourly</Option>
              <Option value="cubic_foot">Cubic Foot</Option>
              <Option value="each">Each</Option>
              <Option value="linear_foot">Linear Foot</Option>
            </Select>
          </Form.Item>

          <Form.Item name="isActive" label="Status" valuePropName="checked">
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>

          <Form.Item className="mb-0">
            <Space className="w-full justify-end">
              <Button onClick={() => setIsModalVisible(false)}>Cancel</Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={createMutation.isPending || updateMutation.isPending}
              >
                {editingMetric ? "Update" : "Create"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
